import { isAppLocale, type AppLocale } from './locales.ts'

export const LOCALE_PREFERENCE_STORAGE_KEY = 'wwm.locale.preference'

export type LocalePreferenceMode = 'automatic' | 'explicit'

export interface LocalePreference {
  readonly version: 1
  readonly mode: LocalePreferenceMode
  readonly locale: AppLocale
}

export interface LocaleStorage {
  readonly getItem: (key: string) => string | null
  readonly setItem: (key: string, value: string) => void
}

export interface LoadLocalePreferenceOptions {
  readonly storage: LocaleStorage | null
  readonly browserLanguages: ReadonlyArray<string>
}

export interface LoadedLocalePreference {
  readonly preference: LocalePreference
  readonly persisted: boolean
}

export function automaticLocalePreference(locale: AppLocale): LocalePreference {
  return { version: 1, mode: 'automatic', locale }
}

export function explicitLocalePreference(locale: AppLocale): LocalePreference {
  return { version: 1, mode: 'explicit', locale }
}

export function isLocalePreference(value: unknown): value is LocalePreference {
  if (value === null || typeof value !== 'object') return false
  const record = value as Readonly<Record<string, unknown>>
  // The locale must be a member of APP_LOCALES right now — anything else
  // (e.g. 'fr' today, or any future third locale that isn't in the registry)
  // is rejected. Delegating to isAppLocale keeps this guard in lock-step with
  // the canonical registry instead of duplicating its allowlist inline.
  return record['version'] === 1
    && (record['mode'] === 'automatic' || record['mode'] === 'explicit')
    && isAppLocale(record['locale'])
}

export function serializeLocalePreference(preference: LocalePreference): string {
  return JSON.stringify(preference)
}

export function persistLocalePreference(
  storage: LocaleStorage | null,
  preference: LocalePreference,
): boolean {
  if (storage === null) return false
  try {
    storage.setItem(LOCALE_PREFERENCE_STORAGE_KEY, serializeLocalePreference(preference))
    return true
  } catch {
    return false
  }
}

export function loadLocalePreference(
  options: LoadLocalePreferenceOptions,
  detectLocale: (languages: ReadonlyArray<string>) => AppLocale,
): LoadedLocalePreference {
  const detected = automaticLocalePreference(detectLocale(options.browserLanguages))
  if (options.storage === null) return { preference: detected, persisted: false }

  try {
    const raw = options.storage.getItem(LOCALE_PREFERENCE_STORAGE_KEY)
    if (raw !== null) {
      const parsed: unknown = JSON.parse(raw)
      if (isLocalePreference(parsed)) return { preference: parsed, persisted: true }
    }
  } catch {
    return {
      preference: detected,
      persisted: persistLocalePreference(options.storage, detected),
    }
  }

  return {
    preference: detected,
    persisted: persistLocalePreference(options.storage, detected),
  }
}

export function resolveEffectiveLocale(
  preference: LocalePreference,
  guildLocale: AppLocale | null,
): AppLocale {
  return preference.mode === 'explicit'
    ? preference.locale
    : guildLocale ?? preference.locale
}
